import { css, html, LitElement, nothing } from 'lit'
import { customElement, query } from 'lit/decorators.js'
import { register, SwiperContainer } from 'swiper/element/bundle'
import { SliderElementModel, SliderSettingsElementModel } from '../api/ContentDeliveryApi.schemas'
import { renderElement } from '../RenderElement'
import { UmbracoPage } from '../ContentLoader'
register()

const customStyles = `
    --swiper-theme-color: light-dark(var(--color-button-dark), var(--color-button-light));
    --swiper-navigation-size: 24px;
    --swiper-navigation-sides-offset: var(--hero-outer-margin);

    --swiper-pagination-color: var(--color-primary-sand-600);
    --swiper-pagination-bullet-inactive-color: var(--color-primary-sand-400);
     --swiper-pagination-bullet-inactive-opacity: 1;
    --swiper-pagination-bullet-size: 16px;
    --swiper-pagination-bullet-horizontal-gap: var(--space-50);
`

@customElement('lindberg-slider')
export class Slider extends LitElement {

    static styles = css`
        :host {
            display: block;
            contain: inline-size;
            min-width: 0;
            width: -webkit-fill-available;
            width: -moz-available;
            width: stretch;
            overflow: hidden;
        }
    `
    @query('swiper-container') swiperContainer!: SwiperContainer

	data!: { content: SliderElementModel; settings?: SliderSettingsElementModel }
    root!: UmbracoPage

    // Computed up front so swiper initialises with the right layout instead of
    // starting at one slide per view and shrinking once the ResizeObserver fires
    layoutParams() {
        if(this.data.settings?.properties?.variant == "multiple"){
            const slideCount = this.data.content.properties.slides?.items?.length ?? 0
            let slidesPerView = this.getVariableValue('--breakpoint') == 'X-Small' ? 1.25 : 3.25
            if(slideCount <= Math.ceil(slidesPerView)) slidesPerView = slideCount
            return { freeMode: true, spaceBetween: parseInt(this.getVariableValue('--grid-gutter')) || 0, slidesPerView }
        }
        return { freeMode: false, spaceBetween: 0, slidesPerView: 1 }
    }

    resize() {
        const slider = this.swiperContainer.swiper
        Object.assign(slider.params, this.layoutParams())
        slider.update()
    }

    getVariableValue(variableName: string): string {
        return getComputedStyle(this).getPropertyValue(variableName).trim()
    }

    firstUpdated() {
        if(this.data.settings?.properties?.backgroundColor?.value) this.style.setProperty('background', this.data.settings.properties.backgroundColor.value)

        // Only react to width changes and defer to the next frame: swiper.update() changes slide
        // sizes (and thereby our height) which would otherwise re-trigger the observer in the same frame
        let lastWidth = -1
        let frame = 0
        const observer = new ResizeObserver(([entry]) => {
            const width = Math.round(entry.contentRect.width)
            if (width === lastWidth) return
            lastWidth = width
            cancelAnimationFrame(frame)
            frame = requestAnimationFrame(() => this.resize())
        })
        observer.observe(this)
    }
    

	render() {
		const layout = this.layoutParams()
		return html`
            <swiper-container .freeMode=${layout.freeMode} .spaceBetween=${layout.spaceBetween} .slidesPerView=${layout.slidesPerView} .navigation=${this.data.settings?.properties?.navigation || false} .pagination=${this.data.settings?.properties?.pagination || false} .loop=${this.data.settings?.properties?.loop} .autoplay=${
			this.data.settings?.properties?.autoplayDelay ? { delay: this.data.settings?.properties.autoplayDelay * 1000 } : false
		} style=${customStyles}>
                ${this.data.content.properties?.slides?.items.map(x => html`<swiper-slide>${renderElement(x, this.root)}</swiper-slide>`)}
            </swiper-container>
        `
	}
}
